import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput, isValidIndianMobile } from '@/components/ui/phone-input';
import { EcregiSeal } from '@/components/ui/ecregi-seal';
import { completeClientSignup } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useSignupStore } from '@shared/stores/signup-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { ME_QUERY_KEY } from '@shared/hooks/use-me';

// Post-signup wizard. Reached when /auth/otp/verify returned SIGNUP_REQUIRED.
// The design mocks show a multi-step form with progress; for now we ship a single
// compact form with just the fields the API requires (firstName, lastName,
// optional mobile). Additional preference-collection steps can be added later
// without changing the signup call.
export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signup = useSignupStore((s) => ({ token: s.signupToken, email: s.email }));
  const clearSignup = useSignupStore((s) => s.clear);
  const setSession = useAuthStore((s) => s.setSession);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');

  const complete = useMutation({
    mutationFn: () =>
      completeClientSignup({
        signupToken: signup.token as string,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobile: mobile.trim() || undefined,
      }),
    onSuccess: (session) => {
      getTokenStore().setAccessToken(session.accessToken, 'client');
      setSession(session.user);
      clearSignup();
      // Prime the useMe() cache with the just-created client before
      // navigating — otherwise a stale `me` from a previous session on
      // this browser could route this fresh client away from /client/home.
      qc.setQueryData(ME_QUERY_KEY, {
        ...session.user,
        avatarUrl: null,
        emailVerifiedAt: null,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      });
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate('/client/home', { replace: true });
    },
  });

  if (!signup.token) {
    return (
      <div className="mx-auto max-w-xl px-6 py-12 text-center">
        <p className="text-gray-600">
          Your signup session has expired. Please sign in again to continue.
        </p>
        <Button className="mt-4" onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    if (mobile && !isValidIndianMobile(mobile)) return;
    complete.mutate();
  }

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-card">
        <div className="flex justify-center">
          <EcregiSeal size={64} />
        </div>
        <h1 className="mt-5 text-center text-xl font-semibold text-gray-900">
          Tell us a bit about yourself
        </h1>
        <p className="mt-1 text-center text-sm text-gray-600">
          We&rsquo;ll personalise services for <span className="font-medium">{signup.email}</span>.
        </p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">First name</label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Last name</label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Mobile <span className="text-gray-400">(optional)</span>
            </label>
            <PhoneInput value={mobile} onChange={setMobile} />
            {mobile && !isValidIndianMobile(mobile) ? (
              <p className="mt-1 text-xs text-red-600">Enter a 10-digit Indian mobile number.</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">Indian numbers only.</p>
            )}
          </div>
          {complete.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(complete.error)}</p>
          ) : null}
          <Button type="submit" block disabled={complete.isPending}>
            {complete.isPending ? 'Creating account…' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
